import { createSelector } from '@reduxjs/toolkit';
import { START_SESSION_EARLIER_TIMEMARK_IN_MINS } from '../../constants/app-constants';
import { Booking } from '../../core/models/booking';
import { SoundtrackOptionDTO } from '../../infra/adapters/luminara/soundtrack/dto/soundtrack-options-dto';
import { canCancelBookingAsHealer, getClientFullName, getClientInitials, isClientNoShow, isPastNoShowDeadline } from '../../util/booking-util';
import {
  calculateMinuteDifference,
  calculateTimeDifference,
  convertToDate,
  getCurrentTime,
  getPrettyDate,
  getPrettyTime,
  minutesToMilliseconds,
} from '../../util/time-util';
import { HealerDashboardAction, HealerDashboardState } from '../reducers/healer-dashboard.reducer';
import { AppState } from '../store';

const VALID_STATUSES_TO_START_SESSION = ['NOT_STARTED'];
const VALID_STATUSES_TO_BE_REDIRECTED_TO_HEALLO = ['STARTED'];
const VALID_STATUSES_TO_BE_REDIRECTED_TO_MEETING = ['HEALLO_COMPLETED', 'MEETING_IN_PROGRESS'];
const VALID_STATUSES_TO_SHOW_REJOIN_SESSION_BUTTON: string[] = [...VALID_STATUSES_TO_BE_REDIRECTED_TO_HEALLO, ...VALID_STATUSES_TO_BE_REDIRECTED_TO_MEETING];
export type HealerDashboardPresentation = {
  loadingDataError: boolean;
  showMusicSelection: boolean;
  hasUpcomingSessions: boolean;
  showLoader: boolean;
  errors: string[];
  firstUpcoming?: {
    id?: string;
    shouldHide: boolean;
    clientLink: string;
    clientFullName?: string;
    clientInitials?: string;
    clientPicture?: string;
    bookingDate?: string;
    bookingTime?: string;
    showStartSessionBtn: boolean;
    showRejoinSessionBtn: boolean;
    canCancel: boolean;
    sessionDurationInMins: number;
  };
  remainingUpcomingSessions: [
    {
      id?: string;
      clientLink: string;
      clientFullName?: string;
      clientInitials?: string;
      clientPicture?: string;
      bookingDate?: string;
      bookingTime?: string;
      canCancel: boolean;
      sessionDurationInMins: number;
    }
  ];
};

export type MusicSelectionPresentation = {
  soundtrackOptions: SoundtrackOptionDTO[];
  selectedSoundtrack?: SoundtrackOptionDTO;
  isPlaying: boolean;
  showSoundtrackVolumeLevelBar: boolean;
};

const getHealerDashboard = (state: AppState) => state.healerDashboard;

export const selectMusicSelectionPresentation = ({ healerDashboard }: AppState): MusicSelectionPresentation => {
  return healerDashboard.musicSelectionPresentation;
};
export const selectHealerDashboardPresentation = createSelector([getHealerDashboard], (healerDashboard) => {
  return {
    showMusicSelection: healerDashboard.showMusicSelection,
    loadingDataError: hasErrors(healerDashboard),
    hasUpcomingSessions: hasUpcomingSessions(healerDashboard),
    showLoader: healerDashboard.showLoader,
    errors: healerDashboard.dataLoadErrors,
    firstUpcoming: {
      id: healerDashboard.firstUpcomingSession?.id,
      clientLink: window.origin + '/dashboard/' + healerDashboard.firstUpcomingSession?.client?.id,
      clientFullName: getClientFullName(healerDashboard.firstUpcomingSession),
      clientPicture: healerDashboard.firstUpcomingSession?.client?.avatar,
      bookingDate: getPrettyDate(healerDashboard.firstUpcomingSession?.bookedTime),
      bookingTime: getPrettyTime(healerDashboard.firstUpcomingSession?.bookedTime),
      clientInitials: getClientInitials(healerDashboard.firstUpcomingSession),
      showStartSessionBtn: healerDashboard.canStartSession,
      showRejoinSessionBtn: shouldShowRejoinSessionBtn(healerDashboard),
      shouldHide: isClientNoShow(healerDashboard.firstUpcomingSession?.bookedTime, healerDashboard.firstUpcomingSession?.sessionStatus),
      canCancel: canCancelBookingAsHealer(healerDashboard.firstUpcomingSession),
      sessionDurationInMins: calculateMinuteDifference(healerDashboard.firstUpcomingSession?.bookedTime, healerDashboard.firstUpcomingSession?.endTime),
    },
    remainingUpcomingSessions: healerDashboard.remainingUpcomingSessions.map((session) => {
      return {
        id: session?.id,
        clientLink: window.origin + '/dashboard/' + session?.client?.id,
        clientFullName: getClientFullName(session),
        clientPicture: session.client?.avatar,
        bookingDate: getPrettyDate(session?.bookedTime),
        bookingTime: getPrettyTime(session?.bookedTime),
        clientInitials: getClientInitials(session),
        canCancel: canCancelBookingAsHealer(session),
        sessionDurationInMins: calculateMinuteDifference(session?.bookedTime, session?.endTime),
      };
    }),
  } as HealerDashboardPresentation;
});

function hasUpcomingSessions(healerDashboardState: HealerDashboardState) {
  return (
    healerDashboardState.hasUpcomingSessions &&
    (!isClientNoShow(healerDashboardState.firstUpcomingSession?.bookedTime, healerDashboardState.firstUpcomingSession?.sessionStatus) ||
      healerDashboardState.remainingUpcomingSessions.length > 0)
  );
}

const hasErrors = (healerDashboard: HealerDashboardState): boolean => {
  return healerDashboard.dataLoadErrors.length > 0;
};

export const canStartSession = (booking?: Booking): boolean => {
  return booking != null && VALID_STATUSES_TO_START_SESSION.includes(booking?.sessionStatus) && isWithinSessionStartTimeRange(booking?.bookedTime);
};

const isWithinSessionStartTimeRange = (bookedTime: string | Date | undefined) => {
  if (!bookedTime) return false;
  return isNotEarlyForStartingSession(bookedTime) && !isPastNoShowDeadline(bookedTime);
};

const isNotEarlyForStartingSession = (bookedTime: string | Date) => {
  const now = getCurrentTime();
  bookedTime = convertToDate(bookedTime);

  const diff = calculateTimeDifference(bookedTime, now);
  return diff < minutesToMilliseconds(START_SESSION_EARLIER_TIMEMARK_IN_MINS);
};

export function rejoinShouldRedirectToMeetingScreen(booking?: Booking): boolean {
  return booking?.sessionStatus !== undefined && VALID_STATUSES_TO_BE_REDIRECTED_TO_MEETING.includes(booking.sessionStatus);
}

export function rejoinShouldRedirectToHealloScreen(booking?: Booking): boolean {
  return booking?.sessionStatus !== undefined && VALID_STATUSES_TO_BE_REDIRECTED_TO_HEALLO.includes(booking.sessionStatus);
}

export function shouldShowRejoinSessionBtn(healerDashboard: HealerDashboardState): boolean {
  return (
    healerDashboard.firstUpcomingSession?.sessionStatus != null &&
    VALID_STATUSES_TO_SHOW_REJOIN_SESSION_BUTTON.includes(healerDashboard.firstUpcomingSession.sessionStatus)
  );
}

export const bookingCancelledFailure = (err: any) => ({ type: 'BOOKING_CANCELLED_BY_HEALER_FAILURE', payload: err } as HealerDashboardAction);

export const displayMusicSelection = () => ({ type: 'SET_SHOW_MUSIC_SELECTION', payload: true } as HealerDashboardAction);
export const hideMusicSelection = () => ({ type: 'SET_SHOW_MUSIC_SELECTION', payload: false } as HealerDashboardAction);

export const setDataLoadingError = (error: any) => ({ type: 'SET_DATA_LOADING_ERROR', payload: error } as HealerDashboardAction);
export const markSessionAsStarted = () => ({ type: 'MARK_FIRST_UPCOMING_SESSION_AS_STARTED' } as HealerDashboardAction);

export const setMusicPreviewPlaying = (playing: boolean) => ({ type: 'SET_MUSIC_PREVIEW_PLAYING', payload: playing } as HealerDashboardAction);
export const setSelectedSoundtrack = (selectedSoundtrack?: SoundtrackOptionDTO) =>
  ({ type: 'SET_SELECTED_SOUNDTRACK_FOR_SESSION', payload: selectedSoundtrack } as HealerDashboardAction);
